import { useState, useMemo } from 'react';
import {
  Building2, Search, Users, GraduationCap, MapPin,
  ChevronLeft, ChevronRight, Eye, TrendingUp, TrendingDown,
  Minus, Phone, Mail, UserCheck, BookOpen, BarChart3,
  X, ExternalLink, Calendar, Clock, Landmark,
} from 'lucide-react';
import { useCrud } from '../../api/hooks/useCrud';
import { useAuthStore } from '../../store/authStore';
import { useMunicipalConfig } from './hooks/useMunicipalConfig';
import { Modal } from '../../components/ui/Modal';
import './escolas-municipal.css';

interface Escola {
  id: string; nome: string; codigo: string; tipoRede: string; endereco?: string; cidade?: string; uf?: string;
  telefone?: string; email?: string; nomeDiretor?: string; codigoINEP?: string; municipioId?: string; ativo: boolean;
  municipio?: { id: string; nome: string; estado?: { id: string; nome: string; sigla: string } };
  _count?: { turmas: number; alunos: number; professorEscolas?: number };
  frequenciaDia?: number | null;
  criadoEm: string;
}

const TIPO_COLORS: Record<string, { bg: string; text: string }> = {
  MUNICIPAL: { bg: 'rgba(30,64,175,.1)', text: '#1e40af' },
  ESTADUAL: { bg: 'rgba(99,102,241,.1)', text: '#6366f1' },
  FEDERAL: { bg: 'rgba(5,150,105,.1)', text: '#059669' },
  PARTICULAR: { bg: 'rgba(245,158,11,.1)', text: '#d97706' },
};

/**
 * Página de Escolas para o Painel Municipal.
 * Somente consulta — sem criar, editar ou excluir.
 */
export function EscolasMunicipal() {
  const user = useAuthStore((s) => s.user);
  const municipioId = user?.municipioId;
  const { data, isLoading } = useCrud<Escola>('escolas', { ...(municipioId ? { municipioId } : {}), ativo: 'true' });
  const { config } = useMunicipalConfig();
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);
  const [detailEscola, setDetailEscola] = useState<Escola | null>(null);

  // Lista estrita de escolas ativas da rede municipal
  const escolasList = useMemo(
    () => (data || []).filter((e) => e.ativo && e.tipoRede === 'MUNICIPAL'),
    [data]
  );

  // Título dinâmico (ex: "Escolas de Belo Horizonte em MG")
  const tituloDinamico = useMemo(() => {
    const nome = config?.cidade || config?.nome || (escolasList[0]?.municipio?.nome) || (escolasList[0]?.cidade);
    const uf = config?.uf || (escolasList[0]?.municipio?.estado?.sigla) || (escolasList[0]?.uf);
    if (nome && uf) {
      return `Escolas de ${nome} em ${uf}`;
    }
    if (nome) {
      return `Escolas de ${nome}`;
    }
    return 'Escolas da Rede Municipal';
  }, [config, escolasList]);

  // Estatísticas
  const stats = useMemo(() => {
    const total = escolasList.length;
    const totalAlunos = escolasList.reduce((a, e) => a + (e._count?.alunos || 0), 0);
    const totalTurmas = escolasList.reduce((a, e) => a + (e._count?.turmas || 0), 0);
    const tipoCount: Record<string, number> = {};
    escolasList.forEach(e => { tipoCount[e.tipoRede] = (tipoCount[e.tipoRede] || 0) + 1; });
    return { total, totalAlunos, totalTurmas, tipoCount };
  }, [escolasList]);

  // Filtro
  const escolasFiltradas = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return escolasList.filter(e => {
      const matchBusca = !q || [
        e.nome, e.codigo, e.tipoRede, e.cidade, e.uf, e.nomeDiretor, e.codigoINEP,
      ].filter(Boolean).join(' ').toLowerCase().includes(q);
      return matchBusca;
    });
  }, [escolasList, searchQuery]);

  // Paginação
  const totalPages = Math.max(1, Math.ceil(escolasFiltradas.length / pageSize));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedEscolas = useMemo(() => {
    const start = (currentPageSafe - 1) * pageSize;
    return escolasFiltradas.slice(start, start + pageSize);
  }, [escolasFiltradas, currentPageSafe, pageSize]);

  // Reset page on search change
  const handleSearch = (v: string) => { setSearchQuery(v); setCurrentPage(1); };

  const renderTipoBadge = (tipoRede: string) => {
    const c = TIPO_COLORS[tipoRede] || { bg: '#f3f4f6', text: '#6b7280' };
    return (
      <span style={{
        padding: '3px 10px', borderRadius: 6, fontSize: '.72rem', fontWeight: 700,
        background: c.bg, color: c.text, letterSpacing: '.02em',
      }}>
        {tipoRede}
      </span>
    );
  };

  const renderFreqIndicator = (pct: number | null | undefined) => {
    if (pct == null) {
      return (
        <span style={{ fontSize: '.72rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Sem dados</span>
      );
    }
    const color = pct >= 90 ? '#059669' : pct >= 80 ? '#d97706' : '#dc2626';
    const Icon = pct >= 90 ? TrendingUp : pct >= 80 ? Minus : TrendingDown;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <div style={{
          width: 48, height: 6, borderRadius: 3, background: `${color}20`, overflow: 'hidden',
        }}>
          <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3, background: color, transition: 'width .3s' }} />
        </div>
        <span style={{ fontSize: '.78rem', fontWeight: 700, color, minWidth: 36, textAlign: 'right' }}>{pct}%</span>
        <Icon size={13} style={{ color, flexShrink: 0 }} />
      </div>
    );
  };

  return (
    <div className="admin-page animate-fadeIn escolas-mun-page">

      {/* ═══ HERO STRIP — Padrão Banco de Mídias ═══ */}
      <div className="mdm-hero" style={{ marginBottom: 12 }}>
        <div className="mdm-hero-circle c1" />
        <div className="mdm-hero-circle c2" />
        <div className="mdm-hero-content">
          <div style={{ minWidth: 0, flexShrink: 1 }}>
            <div className="mdm-hero-badge">
              <Building2 size={10} /> REDE MUNICIPAL
            </div>
            <h1 className="mdm-hero-title">{tituloDinamico}</h1>
            <p className="mdm-hero-subtitle">
              {stats.total} escola(s) ativa(s) na rede · {stats.totalAlunos.toLocaleString('pt-BR')} alunos matriculados
            </p>
          </div>
          <div className="mdm-hero-stats">
            {[
              { icon: Building2, val: stats.total, lbl: 'Escolas', clr: '#93c5fd' },
              { icon: Users, val: stats.totalAlunos.toLocaleString('pt-BR'), lbl: 'Alunos', clr: '#a5f3fc' },
              { icon: BookOpen, val: stats.totalTurmas, lbl: 'Turmas', clr: '#fbbf24' },
              { icon: Landmark, val: 1, lbl: 'Município', clr: '#86efac' },
            ].map((s, i) => (
              <div key={i} className="mdm-hero-stat">
                <div className="mdm-hero-stat-icon" style={{ background: `${s.clr}20`, color: s.clr }}>
                  <s.icon size={12} />
                </div>
                <div>
                  <div className="mdm-hero-stat-value">{s.val}</div>
                  <div className="mdm-hero-stat-label">{s.lbl}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ TABELA PROFISSIONAL ═══ */}
      <div className="escolas-mun-table-container">
        {/* Toolbar */}
        <div className="escolas-mun-toolbar">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
            <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 360 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
              <input
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                placeholder="Buscar escola, código, diretor..."
                className="escolas-mun-search"
              />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.75rem', color: 'var(--text-muted)' }}>
            <span>{escolasFiltradas.length} escola(s)</span>
            <select value={pageSize} onChange={e => { setPageSize(+e.target.value); setCurrentPage(1); }} className="escolas-mun-select" style={{ width: 80 }}>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        {/* Header */}
        <div className="escolas-mun-list-header escolas-mun-grid">
          <span>Escola</span>
          <span style={{ textAlign: 'center' }}>Alunos</span>
          <span style={{ textAlign: 'center' }}>Turmas</span>
          <span className="escolas-mun-hide-md">Frequência</span>
          <span style={{ textAlign: 'center' }}>Rede</span>
          <span style={{ textAlign: 'center' }}>Ação</span>
        </div>

        {/* List */}
        <div className="escolas-mun-list-scroll">
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: '.85rem' }}>Carregando escolas...</div>
          ) : paginatedEscolas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: '.85rem' }}>Nenhuma escola encontrada</div>
          ) : paginatedEscolas.map((escola, idx) => {
            const freq = escola.frequenciaDia;
            const cidade = escola.municipio?.nome || escola.cidade || '';
            const uf = escola.municipio?.estado?.sigla || escola.uf || '';
            return (
              <div
                key={escola.id}
                className="escolas-mun-row escolas-mun-grid"
                onClick={() => setDetailEscola(escola)}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '.82rem', color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {escola.nome}
                  </div>
                  <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
                    <MapPin size={11} style={{ flexShrink: 0 }} />
                    {cidade}{uf ? ` / ${uf}` : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '.85rem', color: 'var(--text-main)' }}>
                  {escola._count?.alunos || 0}
                </div>
                <div style={{ textAlign: 'center', fontWeight: 600, fontSize: '.82rem', color: 'var(--text-muted)' }}>
                  {escola._count?.turmas || 0}
                </div>
                <div className="escolas-mun-hide-md">
                  {renderFreqIndicator(freq)}
                </div>
                <div style={{ textAlign: 'center' }}>
                  {renderTipoBadge(escola.tipoRede)}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); setDetailEscola(escola); }}
                    className="escolas-mun-action-btn"
                    title="Ver detalhes"
                  >
                    <Eye size={15} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        <div className="escolas-mun-pagination">
          <span style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>
            Página {currentPageSafe} de {totalPages}
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPageSafe <= 1}
              className="escolas-mun-page-btn"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPageSafe >= totalPages}
              className="escolas-mun-page-btn"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ═══ MODAL PREMIUM — DETALHES DA ESCOLA ═══ */}
      {detailEscola && (() => {
        const freq = detailEscola.frequenciaDia;
        const freqColor = freq != null ? (freq >= 90 ? '#059669' : freq >= 80 ? '#d97706' : '#dc2626') : '#6b7280';
        const cidade = detailEscola.municipio?.nome || detailEscola.cidade || '—';
        const uf = detailEscola.municipio?.estado?.sigla || detailEscola.uf || '—';
        const alunos = detailEscola._count?.alunos || 0;
        const turmas = detailEscola._count?.turmas || 0;
        const mediaAlunos = turmas > 0 ? Math.round(alunos / turmas) : 0;

        return (
          <Modal
            title=""
            open={!!detailEscola}
            onClose={() => setDetailEscola(null)}
            size="wide"
            contentClassName="escolas-mun-modal-override"
          >
            <div className="emd">
              {/* ── Banner Azul ── */}
              <div className="emd-banner">
                <div className="emd-banner-circle emd-banner-circle-1" />
                <div className="emd-banner-circle emd-banner-circle-2" />
                <button
                  onClick={() => setDetailEscola(null)}
                  className="emd-close-btn"
                  title="Fechar"
                >
                  <X size={18} />
                </button>
                <div className="emd-banner-content">
                  <div className="emd-banner-icon">
                    <Building2 size={24} color="white" />
                  </div>
                  <div className="emd-banner-info">
                    <h2 className="emd-banner-title">{detailEscola.nome}</h2>
                    <div className="emd-banner-meta">
                      <span className="emd-badge" style={{
                        background: 'rgba(255,255,255,.18)', color: 'white', backdropFilter: 'blur(8px)',
                      }}>
                        {detailEscola.tipoRede}
                      </span>
                      <span className="emd-banner-dot">·</span>
                      <MapPin size={12} style={{ opacity: .7 }} />
                      <span>{cidade} / {uf}</span>
                      {detailEscola.codigoINEP && (
                        <>
                          <span className="emd-banner-dot">·</span>
                          <span style={{ opacity: .7 }}>INEP {detailEscola.codigoINEP}</span>
                        </>
                      )}
                      <span className="emd-banner-dot">·</span>
                      <span className="emd-status-pill" style={{
                        background: detailEscola.ativo ? 'rgba(16,185,129,.25)' : 'rgba(239,68,68,.25)',
                        color: detailEscola.ativo ? '#86efac' : '#fca5a5',
                      }}>
                        <span className="emd-status-dot" style={{ background: detailEscola.ativo ? '#34d399' : '#f87171' }} />
                        {detailEscola.ativo ? 'Ativa' : 'Inativa'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── KPI Strip ── */}
              <div className="emd-kpi-strip">
                {[
                  { icon: Users, value: alunos.toLocaleString('pt-BR'), label: 'Alunos Matriculados', color: '#1e40af', bg: 'rgba(30,64,175,.06)' },
                  { icon: BookOpen, value: turmas, label: 'Turmas Ativas', color: '#7c3aed', bg: 'rgba(124,58,237,.06)' },
                  { icon: GraduationCap, value: mediaAlunos, label: 'Média Alunos/Turma', color: '#0891b2', bg: 'rgba(8,145,178,.06)' },
                  { icon: BarChart3, value: freq != null ? `${freq}%` : '—', label: 'Frequencia Media', color: freqColor, bg: `${freqColor}0a` },
                ].map((kpi, i) => (
                  <div key={i} className="emd-kpi" style={{ background: kpi.bg, borderColor: `${kpi.color}15` }}>
                    <div className="emd-kpi-icon" style={{ background: `${kpi.color}12`, color: kpi.color }}>
                      <kpi.icon size={18} />
                    </div>
                    <div>
                      <div className="emd-kpi-value" style={{ color: kpi.color }}>{kpi.value}</div>
                      <div className="emd-kpi-label">{kpi.label}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Body: Two Columns ── */}
              <div className="emd-body">
                {/* Left: Contato & Endereço */}
                <div className="emd-col">
                  <div className="emd-section">
                    <div className="emd-section-header">
                      <UserCheck size={14} style={{ color: '#1e40af' }} />
                      <span>Gestão e Contato</span>
                    </div>
                    <div className="emd-info-rows">
                      <div className="emd-info-row">
                        <div className="emd-info-icon"><UserCheck size={14} /></div>
                        <div>
                          <span className="emd-info-label">Diretor(a)</span>
                          <span className="emd-info-value">{detailEscola.nomeDiretor || 'Não informado'}</span>
                        </div>
                      </div>
                      <div className="emd-info-row">
                        <div className="emd-info-icon"><Phone size={14} /></div>
                        <div>
                          <span className="emd-info-label">Telefone</span>
                          <span className="emd-info-value">{detailEscola.telefone || 'Não informado'}</span>
                        </div>
                      </div>
                      <div className="emd-info-row">
                        <div className="emd-info-icon"><Mail size={14} /></div>
                        <div>
                          <span className="emd-info-label">Email</span>
                          <span className="emd-info-value">{detailEscola.email || 'Não informado'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: Localização & Registro */}
                <div className="emd-col">
                  <div className="emd-section">
                    <div className="emd-section-header">
                      <MapPin size={14} style={{ color: '#7c3aed' }} />
                      <span>Localização e Registro</span>
                    </div>
                    <div className="emd-info-rows">
                      <div className="emd-info-row">
                        <div className="emd-info-icon"><MapPin size={14} /></div>
                        <div>
                          <span className="emd-info-label">Endereço</span>
                          <span className="emd-info-value">{detailEscola.endereco || 'Não informado'}</span>
                        </div>
                      </div>
                      <div className="emd-info-row">
                        <div className="emd-info-icon"><Building2 size={14} /></div>
                        <div>
                          <span className="emd-info-label">Código</span>
                          <span className="emd-info-value" style={{ fontFamily: 'monospace', letterSpacing: '.5px' }}>{detailEscola.codigo}</span>
                        </div>
                      </div>
                      <div className="emd-info-row">
                        <div className="emd-info-icon"><Calendar size={14} /></div>
                        <div>
                          <span className="emd-info-label">Cadastrada em</span>
                          <span className="emd-info-value">{detailEscola.criadoEm ? new Date(detailEscola.criadoEm).toLocaleDateString('pt-BR') : '—'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Frequência Gauge ── */}
              <div className="emd-freq-bar-container">
                <div className="emd-freq-header">
                  <BarChart3 size={14} style={{ color: freqColor }} />
                  <span>Frequência Geral</span>
                  <span className="emd-freq-pct" style={{ color: freqColor }}>{freq}%</span>
                </div>
                <div className="emd-freq-track">
                  <div className="emd-freq-fill" style={{ width: `${freq ?? 0}%`, background: `linear-gradient(90deg, ${freqColor}90, ${freqColor})` }} />
                  {[25, 50, 75].map(mark => (
                    <div key={mark} className="emd-freq-mark" style={{ left: `${mark}%` }} />
                  ))}
                </div>
                <div className="emd-freq-labels">
                  <span>0%</span>
                  <span style={{ color: freq != null ? (freq >= 80 ? '#059669' : '#dc2626') : '#6b7280', fontWeight: 700 }}>
                    {freq == null ? 'Sem chamada' : freq >= 90 ? 'Excelente' : freq >= 80 ? 'Regular' : 'Critico'}
                  </span>
                  <span>100%</span>
                </div>
              </div>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}
